import React from 'react';
import { ExternalLink, Star } from 'lucide-react';
import useDocumentTitle from '../hooks/useDocumentTitle';
import useOpenGraph from '../hooks/useOpenGraph';

const CustomGptPage = () => {
  useDocumentTitle('WP Plugin Architect GPT');
  useOpenGraph({
    title: 'WP Plugin Architect GPT',
    description: 'Create WordPress plugins instantly with our specialized GPT. Get secure, standards-compliant plugins without writing code.',
    image: '/wp-plugin-architect.png'
  });
  
  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-6 text-center text-gray-900">
          WP Plugin Architect
          <span className="text-blue-600 ml-2">GPT</span>
        </h1>
        <div className="text-center mb-8">
          <a 
            href="https://chatgpt.com/g/g-6cqBCrKTn-wp-plugin-architect" 
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white py-3 px-6 rounded-lg text-lg"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLink className="w-5 h-5 mr-2" />
            Open in ChatGPT
          </a>
        </div>

        <div className="bg-white rounded-lg shadow-md p-8 mb-10">
          <div className="flex justify-center mb-8">
            <img 
              src="/wp-plugin-architect.png" 
              alt="WP Plugin Architect GPT" 
              className="w-64 h-64 rounded-lg object-cover shadow-lg"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/256?text=WP+Plugin+Architect";
              }}
            />
          </div>

          <h2 className="text-2xl font-semibold mb-4">What is WP Plugin Architect?</h2>
          <p className="text-gray-700 mb-6">
            WP Plugin Architect is a Custom GPT for ChatGPT that aims to write secure and fully functioning WordPress plugins, 
            adhering to coding standards and following best practices. It takes your ideas and transforms them into 
            ready-to-use WordPress plugins.
          </p>

          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200 mb-8">
            <h3 className="text-xl font-semibold mb-3 text-blue-800">Key Features</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700">
              <li>Creates complete, installable WordPress plugins from simple descriptions</li>
              <li>Follows WordPress coding standards and security best practices</li>
              <li>Generates plugin header, readme.txt, and all necessary files</li>
              <li>Delivers the final plugin as a downloadable zip file, ready for installation</li>
              <li>Free to use, even without a ChatGPT subscription</li>
              <li>No coding experience required</li>
            </ul>
          </div>

          <h2 className="text-2xl font-semibold mb-4">How to Use It</h2>
          <p className="text-gray-700 mb-6">
            Simply click the "Open in ChatGPT" button above and describe the plugin you want to create.
            Be as detailed as possible about what functionality you need. The GPT will guide you through
            the process and ask clarifying questions if needed.
          </p>

          <div className="border-l-4 border-yellow-500 bg-yellow-50 px-6 py-5 rounded-tr-lg rounded-br-lg mb-8">
            <div className="flex items-start">
              <Star className="w-6 h-6 text-yellow-500 mr-3 mt-1 flex-shrink-0" />
              <p className="text-gray-700 italic">
                "WP Plugin Architect is perfect for quickly prototyping plugin ideas or creating simple
                functionality without writing code from scratch. It's like having a WordPress developer
                at your fingertips."
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CustomGptPage;
